import { createContext, useContext } from 'react';

/**
 * Shared attributes that are injected into every Lumina analytics event's `attributes` object.
 *
 * These fields are NOT reliably extracted from `AnalyticsContext` by `FabricAnalyticsListeners`
 * (only standard fields like `source` survive), so they must be placed directly into `attributes`.
 *
 * `source` is intentionally omitted here — it IS correctly extracted by the listener and placed
 * at the top level of the GAS event. Including it in `attributes` as well would be redundant.
 */
export interface LuminaSharedAttributes {
	packageName: string;
	packageVersion: string;
	spanId: string | null;
	traceId: string | null;
}

const DEFAULT_SHARED_ATTRIBUTES: LuminaSharedAttributes = {
	packageName: '',
	packageVersion: '',
	traceId: null,
	spanId: null,
};

/**
 * React context that provides shared attributes to `useLuminaAnalyticsEvents`.
 * Set up by `LuminaLayout` and consumed by every internal analytics call site.
 * @internal
 */
export const LuminaAnalyticsContext = createContext<LuminaSharedAttributes>(DEFAULT_SHARED_ATTRIBUTES);

/**
 * Read the shared attributes from context.
 * @internal
 */
export const useLuminaSharedAttributes = (): LuminaSharedAttributes =>
	useContext(LuminaAnalyticsContext);
